import { ErrorHandler, inject, NgModule } from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxsModule, NgxsModuleOptions, NoopNgxsExecutionStrategy, Store } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { ServiceWorkerModule } from '@angular/service-worker';
import { DateAdapter, MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ThemeResolver } from '@nimbus-library/features/shared/store/theme-store/theme-resolver';
import { TeamsApplicationConfigurationProvider } from '@nimbus-portal/common/services/teams-application-configuration.provider';
import { ErrorCodeProviderService } from '@nimbus-global/features/shared/services/error-code-provider.service';
import { ToolsResolverService } from '@nimbus-portal/common/services/tools-resolver.service';
import { BaseTeamsService } from '@nimbus-library/features/shared/services/team/base/base-teams.service';
import { MicrosoftTeamsService } from '@nimbus-portal/common/services/microsoft-teams.service';
import { ProvisioningSettingsState } from '@nimbus-global/features/settings/store/provisioning-settings/provisioning-settings.state';
import { NavigationModule } from '@nimbus-library/features/navigation/navigation.module';
import { MenuModule } from '@nimbus-portal/features/menu/menu.module';
import { ProvisioningStateObserverService } from '@nimbus-portal/features/provisioning/services/provisioning-state-observer.service';
import { ChartValueTransformer } from '@nimbus-global/features/reporting/helpers/chart-value.transformer';
import { LocalizationModule } from '@nimbus-library/features/localization/localization.module';
import { ILocalizationService } from '@nimbus-library/features/localization/services/i-localization.service';
import { getDashboardDataProviderDataProviders } from '@nimbus-dashboard/features/dashboard/benchmark/dashboard-benchmark';
import { environment } from 'environments/environment';
import { RunbookErrorHandlerService } from '@nimbus-global/features/settings/services/runbook-error-handler.service';
import { ProvisioningSettingsService } from '@nimbus-global/features/settings/services/provisioning-settings.service';
import { RunbookApiClient } from '@nimbus-global/features/settings/services/runbook.api-client';
import { RunbookService } from '@nimbus-global/features/settings/services/runbook.service';
import { ProvisioningSettingsApiClient } from '@nimbus-global/features/settings/services/provisioning-settings.api-client';
import { IGeoLocationService } from '@nimbus-global/features/geo-location/services/i-geo-location.service';
import { GlobalErrorHandler } from '@nimbus-global/features/shared';
import { IDefaultSearchFilterService } from '@nimbus-library/features/graph/services/i-default-search-filter.service';
import { cacheConfigurationBrowserFactory, NimbusCacheBrowserModule } from '@nimbus-library/features/cache/nimbus-cache.browser.module';
import { CacheConfiguration } from '@nimbus-library/features/cache/services/models/cache.configuration';
import { CacheStoragesEnum } from '@nimbus-library/features/cache/services/models/cache.models';
import { BaseErrorCodeProviderService } from '@nimbus-library/features/shared/services/base-error-code-provider.service';
import { ModalModule } from '@nimbus-library/features/modal/modal.module';
import { NimbusChartsModule } from '@nimbus-library/features/charts/nimbus-charts.module';
import { RoutingModule } from '@nimbus-portal/routing';
import { AppComponent } from '@nimbus-portal/app.component';
import { HeaderBarComponent } from '@nimbus-portal/features/header-bar/header-bar.component';
import { NimbusDateAdapter, NimbusDateFormats } from '@nimbus-library/features/localization/services/nimbus-date-adapter';
import { IOrganizationUnitDataService } from '@nimbus-global/features/shared/services/api/organization-unit/organization-unit-data.service';
import { OrganizationUnitDataService } from '@nimbus-portal/common/services/organization-unit/organization-unit-data.service';
import { LandingPageComponent } from '@nimbus-portal/common/components/landing-page/landing-page.component';
import { TrackingModule } from '@nimbus-library/features/tracking/tracking.module';
import { TrackingConfigurationService } from '@nimbus-portal/common/services/tracking-configuration.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { PortalPermissionManagerService } from '@nimbus-global/features/permissions/services/domain/portal-permission-manager.service';
import { ITeamsApplicationConfigurationProvider } from '@nimbus-library/features/microsoft-teams/models/i-teams-application-configuration.provider';
import { TeamsContextModule } from '@nimbus-library/features/microsoft-teams/teams-context.module';
import { LoggingModule } from '@nimbus-library/features/logger/logging.module';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { SIGNALR_DIAGNOSTIC_CLIENT, SIGNALR_LOGGING_ENABLED } from '@nimbus-global/features/signalr/signalR.const';
import {
  AC_PIPLINE_LOGGING_ENABLED,
  acPipelionLoggingEnabledKey,
  signalRDiagnosticClientKey,
  signalRLoggingEnabledKey,
} from '@nimbus-global/features/shared/i-app.configuration';
import { ServiceWorkerBypassInterceptor } from '@nimbus-library/features/shared/interceptors/service-worker-bypass.interceptor';
import { GraphAvatarLimitInterceptor } from '@nimbus-library/features/graph/services/graph-avatar.interceptor';
import {
  ENVIRONMENT_CONFIGURATION_VARIABLE,
  EnvironmentConfigurationService,
  RELEASE_VERSION_CONFIGURATION_VARIABLE,
} from '@nimbus-library/features/configuration';
import { ConnectionAvailabilityBrowserModule } from '@nimbus-library/features/connection-availability/browser/connection-availability.browser.module';
import { PageVisibilityBrowserModule } from '@nimbus-library/features/page-visibility/browser/page-visibility.browser.module';
import { IPermissionService } from '@nimbus-library/features/feature-permission/services/contracts/i-permission.service';
import { PermissionService } from '@nimbus-library/features/feature-permission/services/permission.service';
import { IEnvironmentService } from '@nimbus-library/features/configuration/i-environment.service';
import { AcDefaultFilterService } from '@nimbus-portal/features/attendant-console/services/ac-default-filter.service';
import { ApiQueryParamsInterceptor } from '@nimbus-library/features/shared/interceptors/api-query-params.interceptor';
import { CompanionChatPanelComponent } from '@nimbus-library/features/companion-chat/components/companion-chat-panel/companion-chat-panel.component';
import { CompanionChatResolver } from '@nimbus-library/features/companion-chat/services/companion-chat-resolver';
import { CompanionChatResolver as PortalCompanionChatResolver } from '@nimbus-portal/features/companion-chat/services/companion-chat-resolver.service';
import { CompanionSplitScreenResizerComponent } from '@nimbus-library/features/companion-chat/components/companion-split-screen-resizer/companion-split-screen-resizer.component';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import { ForkRequestsService } from '@nimbus-global/features/shared/fork-requests.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IClientSideLogger } from '@nimbus-library/features/logger/services/client-logger/i-client-side-logger';
import { EnvironmentPortalService } from '../environments/environment-portal.service';
import { GeoLocationService } from './features/geo-location/geo-location.service';
import { LocalizationService } from './features/localization/localization.service';
import { FeaturePermissionModule } from './features/feature-permission/feature-permission.module';
import { NimbusAuthneticationModule } from './features/authentication/nimbus-authentication.module';
import { APP_MODE, AppMode, appModeProvider } from './features/app-mode/app-mode';
import { IInitializationService } from './features/initialization/i-initialization.service';
import { PortalInitializationService } from './features/initialization/portal-initialization.service';
import { NimbusWebCommonModule } from './common/nimbus-web-common.module';
import { WrongLocationPageModule } from './features/wrong-location-page/wrong-location-page.module';
import { WallboardInitializationService } from './features/wallboard/services/infrastructure/wallboard-initialization.service';
import { WallboardLocalizationService } from './features/wallboard/services/infrastructure/wallboard-localization.service';
import { WallboardPermissionManagerService } from './features/wallboard/services/infrastructure/wallboard-permission-manager.service';
import { WallboardOrganizationUnitDataService } from './features/wallboard/services/infrastructure/wallboard-organization-unit-data.service';
import { NimbusWallboardSignalRHub } from './features/wallboard/services/signalr/nimbus-wallboard-signalr.hub';

export function teamsApplicationConfigurationProviderFactory(
  environmentConfigurationService: EnvironmentConfigurationService,
): TeamsApplicationConfigurationProvider {
  return new TeamsApplicationConfigurationProvider(environmentConfigurationService);
}

export function signalRDiagnosticClientFactory(): boolean {
  return sessionStorage && sessionStorage.getItem(signalRDiagnosticClientKey) === 'true';
}

export function signalRLoggingEnabledFactory(): boolean {
  return sessionStorage && sessionStorage.getItem(signalRLoggingEnabledKey) === 'true';
}

export function acPipelineLoggingEnabledFactory(): boolean {
  return sessionStorage && sessionStorage.getItem(acPipelionLoggingEnabledKey) === 'true';
}

export function cacheConfigurationFactory(): CacheConfiguration {
  return inject(APP_MODE) === AppMode.Wallboard ? new CacheConfiguration(CacheStoragesEnum.MEMORY) : cacheConfigurationBrowserFactory();
}

export function nimbusSignalRHubFactory(): NimbusSignalRHub {
  return inject(APP_MODE) === AppMode.Wallboard
    ? (inject(NimbusWallboardSignalRHub) as unknown as NimbusSignalRHub)
    : new NimbusSignalRHub(
        inject(IAuthenticationService),
        inject(ForkRequestsService),
        inject(SIGNALR_DIAGNOSTIC_CLIENT, { optional: true }),
        inject(IClientSideLogger, { optional: true }),
      );
}

export const ngxsConfig: NgxsModuleOptions = {
  selectorOptions: {
    suppressErrors: false,
    injectContainerState: false,
  },
  executionStrategy: NoopNgxsExecutionStrategy,
};

@NgModule({
  declarations: [AppComponent, LandingPageComponent],
  imports: [
    NgxsModule.forRoot([ProvisioningSettingsState], ngxsConfig),
    MenuModule,
    FeaturePermissionModule,
    NavigationModule,
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: true }),
    BrowserModule,
    BrowserAnimationsModule,
    RoutingModule,
    NimbusWebCommonModule,
    ModalModule.forRoot(ThemeResolver),
    NimbusChartsModule,
    PageVisibilityBrowserModule,
    ConnectionAvailabilityBrowserModule,
    NimbusCacheBrowserModule.forRoot(ILogger),
    NimbusAuthneticationModule,
    LoggingModule.forRoot(
      {
        loggingConfiguration: {
          cloudLoggingEnabled: environment.production, // disabled for local development
          consoleLoggingEnabled: true,
          zipkinLoggingEnabled: false,
        },
      },
      'Nimbus.UI.Portal',
    ),
    LocalizationModule.forRoot(
      appModeProvider(ILocalizationService, {
        [AppMode.Default]: LocalizationService,
        [AppMode.Wallboard]: WallboardLocalizationService,
      }),
      {
        fileSuffix: '.json',
        folderPrefix: ['i18n/'],
      },
    ),
    TeamsContextModule.forRoot({ contextItemName: 'Nimbus.UserContext' }, TeamsApplicationConfigurationProvider),
    MatDialogModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production, registrationStrategy: 'registerImmediately' }),
    TrackingModule.forRoot(TrackingConfigurationService),
    WrongLocationPageModule,
    CompanionChatPanelComponent,
    CompanionSplitScreenResizerComponent,
    HeaderBarComponent,
  ],
  providers: [
    { provide: CompanionChatResolver, useClass: PortalCompanionChatResolver },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: ENVIRONMENT_CONFIGURATION_VARIABLE, useValue: 'EnvironmentConfiguration' },
    { provide: RELEASE_VERSION_CONFIGURATION_VARIABLE, useValue: 'ReleaseVersion' },
    { provide: SIGNALR_DIAGNOSTIC_CLIENT, useFactory: signalRDiagnosticClientFactory },
    { provide: SIGNALR_LOGGING_ENABLED, useFactory: signalRLoggingEnabledFactory },
    { provide: CacheConfiguration, useFactory: cacheConfigurationFactory },
    { provide: NimbusSignalRHub, useFactory: nimbusSignalRHubFactory },
    { provide: BaseTeamsService, useClass: MicrosoftTeamsService },
    { provide: BaseErrorCodeProviderService, useClass: ErrorCodeProviderService },
    { provide: IPermissionService, useClass: PermissionService },
    { provide: AC_PIPLINE_LOGGING_ENABLED, useFactory: acPipelineLoggingEnabledFactory },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { closeOnNavigation: true, hasBackdrop: true, backdropClass: 'nimbus-modal-backdrop', autoFocus: '[cdkFocusInitial]' },
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: {
        disabled: true,
      },
    },
    {
      provide: TeamsApplicationConfigurationProvider,
      useFactory: teamsApplicationConfigurationProviderFactory,
      deps: [EnvironmentConfigurationService],
    },
    { provide: ITeamsApplicationConfigurationProvider, useClass: TeamsApplicationConfigurationProvider },
    { provide: IEnvironmentService, useClass: EnvironmentPortalService },
    { provide: IGeoLocationService, useClass: GeoLocationService },
    appModeProvider(IPermissionManagerService, {
      [AppMode.Default]: PortalPermissionManagerService,
      [AppMode.Wallboard]: WallboardPermissionManagerService,
    }),
    EnvironmentConfigurationService,
    ProvisioningStateObserverService,
    RunbookErrorHandlerService,
    ToolsResolverService,
    ChartValueTransformer,
    ProvisioningSettingsService,
    MatNativeDateModule,
    RunbookApiClient,
    RunbookService,
    ProvisioningSettingsApiClient,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: DateAdapter, useClass: NimbusDateAdapter, deps: [MAT_DATE_LOCALE, Store] },
    { provide: MAT_DATE_FORMATS, useValue: NimbusDateFormats },
    { provide: IDefaultSearchFilterService, useClass: AcDefaultFilterService },
    OrganizationUnitDataService,
    LocalizationService,
    appModeProvider(IOrganizationUnitDataService, {
      [AppMode.Default]: OrganizationUnitDataService,
      [AppMode.Wallboard]: WallboardOrganizationUnitDataService,
    }),
    appModeProvider(IInitializationService, {
      [AppMode.Default]: PortalInitializationService,
      [AppMode.Wallboard]: WallboardInitializationService,
    }),
    { provide: HTTP_INTERCEPTORS, useClass: ServiceWorkerBypassInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: GraphAvatarLimitInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiQueryParamsInterceptor, multi: true },
    ...getDashboardDataProviderDataProviders(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
